import React, { createContext, useContext, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode } from '@lexical/rich-text';
import {
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
} from 'lexical';

import EditorContextProvider, { EditorContext } from './EditorContextProvider';
import AlternativeTextPanel from '../AlternativeTextPanel/AlternativeTextPanel';
import { $isImageNode, ImageNode } from './nodes/ImageNode';
import { ImagePlugin } from './plugins/ImagePlugin';
import UploadImageButton from './UploadImageButton';

export const ImageContext = createContext<{
  uploadedImages: { imageUrl: string; altText: string }[];
  addImage: (img: { imageUrl: string; altText: string }) => void;
  setUploadedImages: React.Dispatch<
    React.SetStateAction<{ imageUrl: string; altText: string }[]>
  >;
}>({
  uploadedImages: [],
  addImage: () => {},
  setUploadedImages: () => {},
});

function getCurrentImageUrlsFromEditor(): {
  imageUrl: string;
  altText: string;
}[] {
  const imageData: { imageUrl: string; altText: string }[] = [];

  const traverse = (node: any) => {
    if ($isImageNode(node)) {
      imageData.push({
        imageUrl: node.__src,
        altText: node.getAltText() || '',
      });
    }
    if ($isElementNode(node)) {
      node.getChildren().forEach(traverse);
    }
  };

  traverse($getRoot());
  return imageData;
}

const editorConfig = {
  namespace: 'MainEditor',
  theme: {
    paragraph: 'mb-2',
    text: {
      bold: 'font-bold',
      italic: 'italic',
      underline: 'underline',
    },
    heading: {
      h1: 'text-3xl font-bold mb-2',
      h2: 'text-2xl font-bold mb-2',
      h3: 'text-xl font-bold mb-2',
    },
  },
  nodes: [HeadingNode, ImageNode],
  onError(error: any) {
    console.error(error);
  },
};

type EditorDropContainerProps = {
  setPostContent: (content: string) => void;
};

const EditorDropContainer = ({ setPostContent }: EditorDropContainerProps) => {
  const { editor } = useContext(EditorContext);
  const { uploadedImages, addImage, setUploadedImages } =
    useContext(ImageContext);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/'),
    );
    if (files.length === 0) return;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const src = reader.result as string;

        // 1) 커서 위치에 ImageNode 생성/삽입
        editor.update(() => {
          const imageNode = new ImageNode(src, '');
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertNodes([imageNode]);
          } else {
            $getRoot().append(imageNode);
          }
        });

        // 2) ImageContext에도 추가
        addImage({ imageUrl: src, altText: '' });
      };
      reader.readAsDataURL(file);
    });
  };

  return (
    <div
      className="relative flex flex-col w-full h-full max-h-[625px] overflow-y-auto p-2 bg-white border-t border-b border-l"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <RichTextPlugin
        contentEditable={
          <ContentEditable className="w-full h-full outline-none" />
        }
        placeholder={
          <div className="absolute text-gray-400 pointer-events-none top-2 left-2">
            내용을 입력하세요...
          </div>
        }
        ErrorBoundary={({ children }) => <>{children}</>}
      />
      <HistoryPlugin />
      <ImagePlugin />
      <OnChangePlugin
        onChange={(editorState) => {
          editorState.read(() => {
            const root = $getRoot();
            setPostContent(root.getTextContent());

            const newImages = getCurrentImageUrlsFromEditor();
            setUploadedImages(
              newImages.map((newImg) => {
                const existing = uploadedImages.find(
                  (img) => img.imageUrl === newImg.imageUrl,
                );
                return {
                  imageUrl: newImg.imageUrl,
                  altText: existing?.altText || '',
                };
              }),
            );
          });
        }}
      />
    </div>
  );
};

const MainEditor = ({
  postTitle,
  setIsAltTextLoading,
  setIsAltTextSuccess,
}: {
  postTitle: string;
  setIsAltTextLoading: (loading: boolean) => void;
  setIsAltTextSuccess: (success: boolean) => void;
}) => {
  const [uploadedImages, setUploadedImages] = useState<
    { imageUrl: string; altText: string }[]
  >([]);
  const [postContent, setPostContent] = useState('');

  const addImage = (image: { imageUrl: string; altText: string }) => {
    setUploadedImages((prev) => [...prev, image]);
  };

  return (
    <ImageContext.Provider
      value={{ uploadedImages, addImage, setUploadedImages }}
    >
      <div className="flex flex-col mt-[85px]">
        <div className="flex">
          <div className="flex-1 min-h-[400px] relative">
            <LexicalComposer initialConfig={editorConfig}>
              <EditorContextProvider>
                {/* 기존 업로드 버튼 */}
                <div className="absolute left-0 z-10 -top-14">
                  <UploadImageButton />
                </div>
                {/* 드래그앤드롭 지원 에디터 */}
                <EditorDropContainer setPostContent={setPostContent} />
              </EditorContextProvider>
            </LexicalComposer>
          </div>
          <div className="relative max-h-[650px] overflow-visible z-20">
            <AlternativeTextPanel
              postTitle={postTitle}
              postContent={postContent}
              setIsAltTextLoading={setIsAltTextLoading}
              setIsAltTextSuccess={setIsAltTextSuccess}
            />
          </div>
        </div>
      </div>
    </ImageContext.Provider>
  );
};

export default MainEditor;
